import { useEffect, useRef, useState, useCallback } from 'react';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { TERMINAL_THEMES } from '@/config/themes';

type ShellState = 'connecting' | 'open' | 'disconnected' | 'error';

/**
 * Pinned admin tab that attaches to the DBGo host's tmux session via
 * `/ws/admin-shell`. Mirrors the xterm setup from TerminalView but
 * speaks the simpler admin-shell protocol (no tunnel, no resume, no
 * grace period — every reconnect re-attaches to the same tmux session
 * and tmux preserves the screen).
 *
 * Multi-admin: tmux's native multi-client mode means several admins
 * attached at once see and type into the same screen. That's the
 * intended behavior for this feature.
 */
export function AdminShellView() {
  const [state, setState] = useState<ShellState>('connecting');
  const [bgColor, setBgColor] = useState('#0e1015');
  const [errorMsg, setErrorMsg] = useState('');

  const containerRef = useRef<HTMLDivElement>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const resizeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateRef = useRef<ShellState>(state);
  stateRef.current = state;

  const connect = useCallback(() => {
    if (!containerRef.current) return;

    wsRef.current?.close();
    wsRef.current = null;
    terminalRef.current?.dispose();
    terminalRef.current = null;

    setState('connecting');
    setErrorMsg('');

    const ss = useSettingsStore.getState();
    const fontSize = ss.getNumber('terminal.fontSize', 13);
    const fontFamily = ss.get('terminal.fontFamily', "'JetBrains Mono', monospace");
    const cursorBlink = ss.getBool('terminal.cursorBlink', true);
    const scrollback = ss.getNumber('terminal.scrollback', 10000);
    const appTheme = useAppStore.getState().theme;
    const themeKey = appTheme === 'dark' ? 'terminal.theme.dark' : 'terminal.theme.light';
    const themeDefault = appTheme === 'dark' ? 'dbgo-dark' : 'dbgo-light';
    const themeId = ss.get(themeKey, themeDefault);
    const theme = TERMINAL_THEMES.find((t) => t.id === themeId) ?? TERMINAL_THEMES[0];
    setBgColor(theme.colors.background);

    const terminal = new Terminal({
      theme: theme.colors,
      cursorBlink,
      fontSize,
      fontFamily,
      scrollback,
    });

    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.loadAddon(new WebLinksAddon());
    terminal.open(containerRef.current);
    fitAddon.fit();

    terminalRef.current = terminal;
    fitAddonRef.current = fitAddon;

    const token = localStorage.getItem('dbgo_token');
    if (!token) {
      terminal.writeln('\r\n\x1b[31mError: Not authenticated\x1b[0m');
      setState('error');
      return;
    }

    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${location.host}/ws/admin-shell?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: 'open', cols: terminal.cols, rows: terminal.rows }));
    };

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        terminal.write(new Uint8Array(event.data));
        return;
      }
      if (typeof event.data !== 'string') return;
      try {
        const msg = JSON.parse(event.data) as { type: string; message?: string; session?: string };
        if (msg.type === 'opened') {
          setState('open');
          return;
        }
        if (msg.type === 'error') {
          setErrorMsg(msg.message ?? 'Shell error');
          terminal.writeln(`\r\n\x1b[31m${msg.message ?? 'Shell error'}\x1b[0m`);
          setState('error');
          return;
        }
      } catch {
        terminal.write(event.data);
      }
    };

    ws.onclose = () => {
      if (stateRef.current === 'open' || stateRef.current === 'connecting') {
        setState('disconnected');
      }
    };

    ws.onerror = () => {
      if (stateRef.current !== 'error') setState('error');
    };

    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(data);
      }
    });
  }, []);

  // Mount once. Reconnect on demand via the banner button below.
  useEffect(() => {
    connect();
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
      resizeTimer.current = setTimeout(() => {
        const fitAddon = fitAddonRef.current;
        const terminal = terminalRef.current;
        if (!fitAddon || !terminal) return;
        fitAddon.fit();
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'resize',
            cols: terminal.cols,
            rows: terminal.rows,
          }));
        }
      }, 100);
    });
    resizeObserver.observe(container);

    return () => {
      wsRef.current?.close();
      wsRef.current = null;
      terminalRef.current?.dispose();
      terminalRef.current = null;
      fitAddonRef.current = null;
      resizeObserver.disconnect();
      if (resizeTimer.current) clearTimeout(resizeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col h-full" style={{ backgroundColor: bgColor }}>
      {(state === 'disconnected' || state === 'error') && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-dbgo-panel border-b text-xs">
          <span className={state === 'error' ? 'text-accent-red' : 'text-text-secondary'}>
            {state === 'error' ? `Error${errorMsg ? `: ${errorMsg}` : ''}` : 'Disconnected'}
          </span>
          <button
            type="button"
            onClick={connect}
            className="px-2 py-1 text-xs bg-accent-blue text-white rounded-sm hover:bg-accent-blue/90"
          >
            Reconnect
          </button>
        </div>
      )}
      <div ref={containerRef} className="flex-1 min-h-0 p-2" />
    </div>
  );
}
